import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PlayCircle, HelpCircle, Wrench, X, Users, Pin, Clock } from "lucide-react";

const resolve = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export default function Updates() {
  const { user } = useAuth();
  const [feed, setFeed] = useState({ updates: [], by_author: [] });
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ text: "", kind: "project", video_id: "" });
  const [videos, setVideos] = useState([]);
  const [openStory, setOpenStory] = useState(null); // {author_id, items:[]}
  const [storyIdx, setStoryIdx] = useState(0);

  const load = () => api.get("/updates/feed").then((r) => setFeed(r.data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);
  useEffect(() => { api.get("/videos").then((r) => setVideos(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!form.text) return toast.error("Add some text");
    try {
      await api.post("/updates", { ...form, video_id: form.video_id || null });
      setOpenCreate(false);
      setForm({ text: "", kind: "project", video_id: "", pinned: false });
      load();
      toast.success("Posted to your friends");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const togglePin = async (u) => {
    try {
      await api.post("/updates/pin", { update_id: u.id, pinned: !u.pinned });
      toast.success(u.pinned ? "Unpinned" : "Pinned to profile — will not expire");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (!user) return <Navigate to="/login" />;

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs text-primary">// updates · friends only</div>
          <h1 className="mt-2 text-5xl font-black tracking-tighter">Your circle.</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Post personal projects or open enquiries. Only your friends see these — perfect for early prototypes, "who has done this?" questions, and weekend experiments.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/friends"><Button variant="outline" data-testid="updates-friends-link" className="rounded-none"><Users className="mr-2 h-4 w-4" />Friends</Button></Link>
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild><Button data-testid="new-update-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Plus className="mr-2 h-4 w-4" />Post update</Button></DialogTrigger>
            <DialogContent className="max-w-lg rounded-none">
              <DialogHeader><DialogTitle>Post to your friends</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <button data-testid="upd-kind-project" onClick={() => setForm({ ...form, kind: "project" })} className={`flex-1 border p-3 text-left ${form.kind === "project" ? "border-primary bg-primary/5" : "border-border"}`}>
                    <div className="flex items-center gap-2 text-sm font-bold"><Wrench className="h-4 w-4 text-primary" />Project</div>
                    <div className="text-xs text-muted-foreground mt-1">Show what you're building</div>
                  </button>
                  <button data-testid="upd-kind-enquiry" onClick={() => setForm({ ...form, kind: "enquiry" })} className={`flex-1 border p-3 text-left ${form.kind === "enquiry" ? "border-primary bg-primary/5" : "border-border"}`}>
                    <div className="flex items-center gap-2 text-sm font-bold"><HelpCircle className="h-4 w-4 text-primary" />Enquiry</div>
                    <div className="text-xs text-muted-foreground mt-1">Ask your circle for help</div>
                  </button>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Text</Label>
                  <Textarea data-testid="upd-text" rows={5} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="mt-2 rounded-none bg-input" placeholder={form.kind === "project" ? "Sketched a solar dehydrator today…" : "Anyone worked with lignin-based bioadhesives?"} />
                </div>
                {videos.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Attach a Learn video (optional)</Label>
                    <select data-testid="upd-video" value={form.video_id} onChange={(e) => setForm({ ...form, video_id: e.target.value })} className="mt-2 w-full rounded-none bg-input border border-border p-2 text-sm">
                      <option value="">— none —</option>
                      {videos.map((v) => <option key={v.id} value={v.id}>{v.title} · {v.kind}</option>)}
                    </select>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input data-testid="upd-pinned" type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} className="accent-primary" />
                  <span>Keep on my profile (skip 24h auto-expiry)</span>
                </label>
              </div>
              <DialogFooter><Button data-testid="upd-submit" onClick={submit} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Post</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stories bar */}
      {feed.by_author.length > 0 && (
        <div className="mb-10 flex gap-4 overflow-x-auto pb-2">
          {feed.by_author.map((a) => (
            <button key={a.author_id} data-testid={`story-${a.author_id}`} onClick={() => { setOpenStory(a); setStoryIdx(0); }} className="shrink-0 text-center">
              <div className="mx-auto h-16 w-16 border-2 border-primary p-0.5">
                <div className="flex h-full w-full items-center justify-center bg-primary/20 text-xl font-bold text-primary">
                  {a.author_name?.[0]?.toUpperCase()}
                </div>
              </div>
              <div className="mt-2 text-xs uppercase tracking-widest text-muted-foreground max-w-[80px] truncate">{a.author_name}</div>
              <div className="mono text-[10px] text-primary">{a.items.length}</div>
            </button>
          ))}
        </div>
      )}

      {/* Feed list */}
      {feed.updates.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <div className="text-lg font-bold">Your feed is empty</div>
          <div className="mt-2 text-sm text-muted-foreground">Post an update or <Link to="/friends" className="text-primary hover:underline">add friends</Link> to see their projects and enquiries here.</div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {feed.updates.map((u) => {
            const remaining = u.pinned ? null : (u.expires_at ? Math.max(0, new Date(u.expires_at) - new Date()) : null);
            const hoursLeft = remaining != null ? Math.round(remaining / 3600000) : null;
            const isOwn = u.author_id === user.id;
            return (
              <div key={u.id} data-testid={`update-${u.id}`} className={`border p-5 ${u.pinned ? "border-primary" : "border-border"}`}>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center bg-primary/20 text-xs font-bold text-primary">{u.author_name?.[0]?.toUpperCase()}</div>
                    <div>
                      <div className="font-bold text-sm">{u.author_name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{u.author_role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.pinned && <span className="px-2 py-0.5 text-[9px] uppercase tracking-widest border border-primary text-primary flex items-center gap-1"><Pin className="h-3 w-3" />pinned</span>}
                    {!u.pinned && hoursLeft != null && (
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{hoursLeft}h left</span>
                    )}
                    <span className={`px-2 py-0.5 text-[9px] uppercase tracking-widest border ${u.kind === "enquiry" ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>{u.kind}</span>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap">{u.text}</p>
                {u.video && (
                  <div className="mt-3 border border-border overflow-hidden">
                    <video src={resolve(u.video.video_url)} controls poster={u.video.thumbnail_url || undefined} className="w-full aspect-video object-cover" />
                    <div className="p-2 text-xs">{u.video.title}</div>
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{new Date(u.created_at).toLocaleString()}</div>
                  {isOwn && (
                    <button data-testid={`pin-${u.id}`} onClick={() => togglePin(u)} className="flex items-center gap-1 border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:border-primary hover:text-primary">
                      <Pin className="h-3 w-3" />{u.pinned ? "Unpin" : "Keep on profile"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Story viewer */}
      {openStory && (
        <div className="fixed inset-0 z-[60] bg-background/95 flex items-center justify-center p-6" onClick={() => setOpenStory(null)}>
          <div className="relative w-full max-w-md border border-primary/50 bg-background" onClick={(e) => e.stopPropagation()}>
            <button data-testid="story-close" onClick={() => setOpenStory(null)} className="absolute top-3 right-3 z-10 border border-border p-1 bg-background/80 hover:border-primary"><X className="h-4 w-4" /></button>
            <div className="flex gap-1 p-2">
              {openStory.items.map((_, i) => (
                <div key={i} className={`h-0.5 flex-1 ${i === storyIdx ? "bg-primary" : i < storyIdx ? "bg-primary/40" : "bg-border"}`} />
              ))}
            </div>
            <div className="px-4 py-2 text-sm">
              <span className="font-bold">{openStory.author_name}</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">{openStory.items[storyIdx]?.kind}</span>
            </div>
            <div className="p-6 min-h-[300px]">
              <p className="text-lg leading-relaxed whitespace-pre-wrap">{openStory.items[storyIdx]?.text}</p>
              {openStory.items[storyIdx]?.video && (
                <div className="mt-4 border border-border overflow-hidden">
                  <video src={resolve(openStory.items[storyIdx].video.video_url)} controls autoPlay className="w-full aspect-video object-cover" />
                </div>
              )}
            </div>
            <div className="flex border-t border-border">
              <button data-testid="story-prev" onClick={() => setStoryIdx((i) => Math.max(0, i - 1))} disabled={storyIdx === 0} className="flex-1 p-3 text-xs uppercase tracking-widest disabled:opacity-30 hover:bg-primary/5">← Prev</button>
              <button data-testid="story-next" onClick={() => { if (storyIdx < openStory.items.length - 1) setStoryIdx(storyIdx + 1); else setOpenStory(null); }} className="flex-1 p-3 text-xs uppercase tracking-widest hover:bg-primary/5 border-l border-border">{storyIdx < openStory.items.length - 1 ? "Next →" : "Done"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
